/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import {
  DataGrid,
  gridClasses,
  GridFilterModel,
  GridRowParams,
} from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import PageContainer from "../page-container/page-container";
import useClients from "./data/use-clients";
import { getColumns, pageTitle } from "./data/data-grid";
import ClientCreate from "./client-create";
import { useNotification } from "../../context/notification-provider";
import { useDialogs } from "../dialog/use-dialog";
import ClientUpdate from "./client-update";
import ClientDelete from "./client-delete";
import { ClientInformation } from "./data/types";
import { updateMappingComplete } from "./data/clients-service";

const INITIAL_PAGE_SIZE = 10;

const ClientList = () => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { confirm } = useDialogs();
  const { data, error: loadingError, isLoading, mutate } = useClients();

  const [dialog, setDialog] = React.useState<"create" | "edit" | "delete" | "">(
    "",
  );
  const [selectedClient, setSelectedClient] = React.useState<any>(null);

  const [filterModel, setFilterModel] = React.useState<GridFilterModel>(
    searchParams.get("filter")
      ? JSON.parse(searchParams.get("filter") ?? "")
      : { items: [] },
  );

  const handleCreateClick = React.useCallback(() => {
    setDialog("create");
  }, []);

  const initialState = React.useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );

  const handleFilterModelChange = React.useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      if (
        model.items.length > 0 ||
        (model.quickFilterValues && model.quickFilterValues.length > 0)
      ) {
        searchParams.set("filter", JSON.stringify(model));
      } else {
        searchParams.delete("filter");
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? "?" : ""}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleOnRefreshClick = React.useCallback(() => {
    showNotification({
      message: "Refreshing client data...",
      severity: "info",
      duration: 2000,
    });
    mutate();
  }, [showNotification, mutate]);

  const handleOnDialogClose = React.useCallback(() => {
    setDialog("");
    setSelectedClient(null);
    mutate();
  }, [mutate]);

  const handleOnEdit = React.useCallback(
    (selectedElement: ClientInformation) => {
      setSelectedClient(selectedElement);
      setDialog("edit");
    },
    [],
  );

  const handleOnDelete = React.useCallback(
    (selectedElement: ClientInformation) => {
      setSelectedClient(selectedElement);
      setDialog("delete");
    },
    [],
  );

  const handleToggleMappingComplete = React.useCallback(
    async (client: ClientInformation) => {
      const newValue = !client.mappingComplete;
      const action = newValue ? "complete" : "incomplete";

      try {
        const result = await confirm(
          `Are you sure you want to mark mapping as ${action} for "${client.name}"?`,
          {
            title: "Confirm Mapping Status Change",
            okText: "Confirm",
            cancelText: "Cancel",
            severity: "warning",
          },
        );

        if (result === true) {
          await updateMappingComplete(client.id, newValue);
          showNotification({
            message: `Mapping marked as ${action} for "${client.name}".`,
            severity: "success",
            duration: 3000,
          });
          mutate();
        }
      } catch (toggleError) {
        showNotification({
          message: `Failed to update mapping status. ${(toggleError as Error).message}`,
          severity: "error",
          duration: 3000,
        });
      }
    },
    [confirm, showNotification, mutate],
  );

  return (
    <PageContainer
      title={pageTitle}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Reload data" placement="right" enterDelay={1000}>
            <div>
              <IconButton
                size="small"
                aria-label="refresh"
                onClick={handleOnRefreshClick}
              >
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
          >
            Create
          </Button>
        </Stack>
      }
    >
      <Box sx={{ flex: 1, width: "100%", height: "100%" }}>
        {loadingError ? (
          <Box sx={{ flexGrow: 1 }}>
            <Alert severity="error">Unable to load client information</Alert>
          </Box>
        ) : (
          <DataGrid
            rows={data || []}
            columns={getColumns({
              onEdit: handleOnEdit,
              onDelete: handleOnDelete,
              onToggleMappingComplete: handleToggleMappingComplete,
            })}
            disableRowSelectionOnClick
            onRowClick={(selectedElement: GridRowParams<ClientInformation>) =>
              handleOnEdit(selectedElement.row)
            }
            showToolbar
            loading={isLoading}
            initialState={initialState}
            pageSizeOptions={[5, INITIAL_PAGE_SIZE, 25]}
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            getRowClassName={(params) =>
              params.row.deleted ? "row-disabled" : ""
            }
            sx={{
              height: "683px",
              [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                outline: "transparent",
              },
              [`& .${gridClasses.columnHeader}`]: {
                backgroundColor: "#f5f6fa",
                outline: "transparent",
              },
              [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                {
                  outline: "none",
                },
              [`& .${gridClasses.row}:hover`]: {
                cursor: "pointer",
              },
              "& .row-disabled": {
                opacity: 0.5,
                pointerEvents: "none",
              },
            }}
            slotProps={{
              loadingOverlay: {
                variant: "circular-progress",
                noRowsVariant: "circular-progress",
              },
              baseIconButton: {
                size: "small",
              },
            }}
          />
        )}
      </Box>
      {dialog === "create" && (
        <ClientCreate
          open={dialog === "create"}
          onClose={handleOnDialogClose}
        />
      )}
      {dialog === "edit" && (
        <ClientUpdate
          open={dialog === "edit"}
          selectedClient={selectedClient}
          onClose={handleOnDialogClose}
        />
      )}
      {dialog === "delete" && (
        <ClientDelete
          open={dialog === "delete"}
          selectedClient={selectedClient}
          onClose={handleOnDialogClose}
        />
      )}
    </PageContainer>
  );
};
export default ClientList;
